if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // If no viewer, bail (it’s required)
        if (!this.elements.viewer) return;

        // Thumbnails are optional
        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );

          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (!btn) return;
            btn.addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
          });

          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event?.detail?.currentElement) return;
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (!mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        // If the corresponding thumbnail doesn't exist (filtered/removed), just skip
        if (!thumbnail) return;
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Try to find requested media
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

        // Fallback: first visible media (not .hide-media), or first media at all
        if (!activeMedia) {
          activeMedia =
            this.elements.viewer.querySelector('[data-media-id]:not(.hide-media)') ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (!activeMedia) return; // nothing to show; bail quietly
        }

        // Activate the chosen media
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        // Only prepend if we actually can
        if (prepend && activeMedia.parentElement) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumb = this.elements.thumbnails.querySelector(
              `[data-target="${activeMedia.dataset.mediaId}"]`
            );
            if (activeThumb?.parentElement) activeThumb.parentElement.prepend(activeThumb);
          }

          if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Scroll current media into view
        window.setTimeout(() => {
          // Scroll the media strip, if present
          if (activeMedia?.parentElement?.scrollTo) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Stacked layout: make sure it’s visible
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
          }
        });

        this.playActiveMedia(activeMedia);

        // Thumbnails sync (optional)
        if (this.elements.thumbnails) {
          const thumb = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          if (thumb) {
            this.setActiveThumbnail(thumb);
            const pos = thumb.dataset.mediaPosition || '';
            this.announceLiveRegion(activeMedia, pos);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button')
          .forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        // Guard against missing slider API
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo?.({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem?.querySelector?.('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem?.querySelector?.('.deferred-media');
        if (deferredMedia?.loadContent) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent?.(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
